<template>
    <LsModal :title="title" track-id="credit_customer_modal">
        <template #body>
            <div v-if="successCopy">
                <LsCopyContent :text="successCopy" />
            </div>
            <div v-else>
                <LsInfoCard class="credit-customer-modal--row">
                    You can manually credit the customer up to $100. Please contact Tier 2 rep for
                    approving an amount over $100.
                </LsInfoCard>

                <form novalidate @submit.prevent="manuallyCredit">
                    <div class="credit-customer-modal--row">
                        <LsInputMasked
                            v-model="amount"
                            mask="currency"
                            track-id="credit_customer_modal-amount"
                            name="amount"
                            label="Amount"
                            :validation="{ required: true, max_value: 100 }"
                            class="credit-customer-modal--currency"
                        />
                    </div>

                    <div class="credit-customer-modal--reference-row">
                        <LsSelect
                            v-model="reference"
                            label="Reference (internal)"
                            track-id="credit_customer_modal-reference"
                            name="reference"
                            :options="referenceOptions"
                            :validation="{ required: true }"
                        />

                        <LsInput
                            v-model="reference_id"
                            track-id="credit_customer_modal-reference_id"
                            name="reference_id"
                            type="number"
                            placeholder="Enter a numerical id"
                            :validation="{ required: true, max: 100 }"
                        />
                    </div>

                    <LsSelect
                        v-model="reference_subcategory"
                        track-id="credit_customer_modal-reference_subcategory"
                        name="subcategory"
                        :options="referenceSubcategoryOptions"
                        label="Subcategory (internal)"
                        class="credit-customer-modal--row"
                        :validation="{ required: true }"
                    />

                    <LsInput
                        v-model="zendesk_ticket_id"
                        track-id="credit_customer_modal-zendesk_ticket_id"
                        label="Zendesk ticket number"
                        name="zendesk_ticket_id"
                        type="number"
                        placeholder="Enter number"
                        class="credit-customer-modal--row credit-customer-modal--zendesk"
                        :validation="{ required: true, min_value: 1000000, max: 8 }"
                    />
                    <LsError v-if="isInvalid" class="credit-customer-modal--row" />

                    <div class="credit-customer-modal--button-row">
                        <LsButton
                            text="Add Credit"
                            track-id="credit_customer_modal-add_credit"
                            :loading="isUpdating"
                            type="submit"
                        />
                        <LsButton
                            text="Cancel"
                            track-id="credit_customer_modal-cancel"
                            outlined
                            text-color="primary"
                            @click="close"
                        />
                    </div>
                </form>
            </div>
        </template>
        <template #footer>
            <div v-if="successCopy">
                <LsButton
                    text="Back to Billing"
                    track-id="credit_customer_modal-back_to_billing"
                    outlined
                    text-color="primary"
                    @click="close"
                />
            </div>
        </template>
    </LsModal>
</template>

<script>
import VueTypes from 'vue-types';
import { lsErrorService } from 'ls-vue-common';
import _ from 'lodash';
import LsInputMasked from '@/components/common/LsInputMasked/LsInputMasked.vue';
import {
    LsModal,
    LsButton,
    LsInfoCard,
    LsInput,
    LsSelect,
    LsCopyContent,
} from '@/components/common';
import Customer from '@/graphql/queries/Customer.gql';
import CreateCustomerBillingCredit from '@/graphql/mutations/CreateCustomerBillingCredit.gql';
import CustomerBilling from '@/graphql/queries/CustomerBilling.gql';
import LsError from '@/components/common/LsError/LsError.vue';

const referenceOptions = [
    { label: "Job Wasn't Completed", value: 'job_incomplete' },
    { label: 'Customer Credit', value: 'credit_customer' },
    { label: 'Stripe Adjustment', value: 'stripe_adjustment' },
    { label: 'Share: NextDoor', value: 'share_nextdoor' },
    { label: 'Share: Facebook', value: 'share_facebook' },
    { label: 'Share: Twitter', value: 'share_twitter' },
    { label: 'Share: SMS', value: 'share_sms' },
    { label: 'Share: Email', value: 'share_email' },
    { label: 'Referral', value: 'referral' },
    { label: 'Deal', value: 'deal' },
    {
        label: 'Property Damage Customer Credit',
        value: 'property_damage_customer_credit',
    },
];

const referenceSubcategoryOptions = [
    { label: 'Partial Completion', value: 'partial_completion' },
    { label: 'LawnTreatment Issue', value: 'lawntreatment' },
    { label: 'Incomplete Service', value: 'incomplete_service' },
    { label: 'Long Grass Fee Issue', value: 'long_grass_fee' },
    { label: 'Property Obstruction', value: 'property_obstruction' },
    { label: 'Scheduling Issue', value: 'scheduling' },
    { label: 'Quality Issue', value: 'quality_issue' },
    { label: 'Pricing / Billing Issue', value: 'pricing_or_billing' },
    { label: 'Cancellation / Pause Issue', value: 'cancellation_or_pause' },
    {
        label: 'Marketing Issue - Crediting for NextDoor etc.',
        value: 'marketing_issue_crediting',
    },
    { label: 'Dispute Resolution', value: 'dispute_resolution' },
    { label: 'Property Damage', value: 'property_damage' },
];

export default {
    name: 'CreditCustomerModal',
    components: {
        LsModal,
        LsButton,
        LsInfoCard,
        LsInputMasked,
        LsInput,
        LsSelect,
        LsError,
        LsCopyContent,
    },
    $_veeValidate: {
        validator: 'new',
    },
    props: {
        title: VueTypes.string.isRequired,
    },
    data() {
        return {
            isInvalid: false,
            isUpdating: false,
            referenceSubcategoryOptions,
            referenceOptions,
            successCopy: null,

            // form data
            amount: '',
            reference: null,
            reference_subcategory: null,
            reference_id: '',
            zendesk_ticket_id: '',
        };
    },
    methods: {
        close() {
            this.$parent.close();
        },
        async manuallyCredit() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateCustomerBillingCredit,
                    variables: {
                        customer_id: _.toNumber(this.$route.params.customer_id),
                        amount: _.toNumber(this.amount),
                        reference: this.reference,
                        reference_subcategory: this.reference_subcategory,
                        reference_id: _.toNumber(this.reference_id),
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                    refetchQueries: [
                        {
                            query: Customer,
                            variables: {
                                id: _.toNumber(this.$route.params.customer_id),
                            },
                        },
                        {
                            query: CustomerBilling,
                            variables: {
                                customer_id: _.toNumber(this.$route.params.customer_id),
                            },
                        },
                    ],
                })
                .then(({ data }) => {
                    this.successCopy = data.createCustomerBillingCredit.copy;
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>

<style lang="scss">
.credit-customer-modal--row {
    margin-bottom: 20px;

    .select {
        width: 330px;
    }

    .select:not(.is-multiple) {
        height: 2.6em;
    }
}
.credit-customer-modal--reference-row {
    display: flex;
    margin-bottom: 20px;

    .select {
        margin-right: 10px;

        select {
            width: 330px;
        }
    }

    input {
        margin-top: 28px;
    }
}

.credit-customer-modal--currency {
    width: 155px;

    .control.has-icons-left .icon {
        width: 2.5em;
    }
}

.credit-customer-modal--zendesk {
    width: 165px;
}

.credit-customer-modal--button-row {
    display: flex;

    button:nth-child(1) {
        margin-right: 10px;
    }
}
</style>
